import { jsxs, jsx } from "react/jsx-runtime";
import { Actions, Action } from "../../ai-elements/actions.js";
import { Shimmer } from "../../ai-elements/shimmer.js";
import brandclub_colors from "../../colors/brandclubColors.js";
import { CopyIcon } from "../../icons/CopyIcon.js";
import { ShareIcon } from "../../icons/ShareIcon.js";
import { ThumbsDownIcon } from "../../icons/ThumbsDownIcon.js";
import { ThumbsUpIcon } from "../../icons/ThumbsUpIcon.js";
import React__default, { useState, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { ImagePreviewOverlay } from "./ImagePreviewOverlay.js";
import { useIsMobile } from "./ConciergeChatAreaV2/useIsMobile.js";
import { toast } from "sonner";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
import { sendMessageFeedback } from "../api.js";
import { marked } from "marked";
import { ConciergeMessagePart } from "./ConciergeMessagePart/index.js";
import DevInfoPanel from "./DevInfoPanel.js";
import { OrderCardList } from "./OrderCard.js";
import { ChainOfThoughtWidget, MessageBubbleWidget, themeColor, customColor, FollowUpQueriesWidget } from "@brandclub/bc-widgets";
const RetryIcon = () => /* @__PURE__ */ jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", children: /* @__PURE__ */ jsx(
  "path",
  {
    d: "M3.502 16.666v-3.333c0-.367.298-.665.665-.665H7.5l.134.014a.665.665 0 0 1 0 1.303l-.134.013H5.475A6.12 6.12 0 0 0 10 16.001c3.06 0 5.586-2.29 5.955-5.25l.03-.131a.665.665 0 0 1 1.29.295l-.05.337A7.33 7.33 0 0 1 10 17.332a7.46 7.46 0 0 1-5.168-2.085v1.42a.665.665 0 0 1-1.33 0m.543-7.417a.665.665 0 0 1-1.32-.165zM10 2.67c1.994 0 3.837.797 5.178 2.093V3.333a.665.665 0 0 1 1.33 0v3.333a.665.665 0 0 1-.665.665H12.51a.665.665 0 0 1 0-1.33h2.015A6.12 6.12 0 0 0 10 3.998 6.003 6.003 0 0 0 4.045 9.25l-.66-.083-.66-.082A7.333 7.333 0 0 1 10 2.668",
    fill: "currentColor"
  }
) });
const ChatMessageComponent = React__default.forwardRef(
  ({
    role,
    content,
    bottomRef,
    isTyping = false,
    isStreaming = false,
    isLastMessage = false,
    messageIndex,
    conversationId,
    feedback,
    followUpQueries = [],
    nextUserMessage,
    orders,
    devInfo,
    onSearch,
    onUpdateFeedback,
    onVariantSelect,
    onSuggestionClick,
    apiBaseUrl,
    isSharedView,
    uiMessageParts,
    transientStatus,
    pdpStacklineSku,
    isOnChatWidget,
    brandLogoUrl,
    followUpsLoading,
    onRetry,
    onOpenShareDialog,
    viewMode
  }, _ref) => {
    const isUser = role === "user";
    const hasUiMessageParts = Array.isArray(uiMessageParts) && uiMessageParts.length > 0;
    const hasAnyContent = content.length > 0 || hasUiMessageParts && ((uiMessageParts == null ? void 0 : uiMessageParts.some((p) => p.type !== "text" || p.text)) ?? false);
    const shimmerActive = !isTyping && isStreaming && !!transientStatus && content.length === 0;
    const [liked, setLiked] = useState((feedback == null ? void 0 : feedback.type) === "like");
    const [disliked, setDisliked] = useState((feedback == null ? void 0 : feedback.type) === "dislike");
    const [copied, setCopied] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);
    const closePreview = useCallback(() => setPreviewImageUrl(null), []);
    const isMobile = useIsMobile();
    const [localSelectedFollowUp, setLocalSelectedFollowUp] = useState(void 0);
    const derivedSelectedFollowUp = useMemo(() => {
      if (!nextUserMessage || !followUpQueries.length) return void 0;
      return followUpQueries.find((query) => query === nextUserMessage);
    }, [nextUserMessage, followUpQueries]);
    const selectedFollowUp = derivedSelectedFollowUp || localSelectedFollowUp;
    const userMessageRef = useRef(null);
    const [isMultiline, setIsMultiline] = useState(false);
    const assistantContentRef = useRef(null);
    const [assistantBubbleWidth, setAssistantBubbleWidth] = useState(void 0);
    const messageWrapperRef = useRef(null);
    const [isRenderedEmpty, setIsRenderedEmpty] = useState(false);
    const hasWidgetParts = useMemo(
      () => Array.isArray(uiMessageParts) && uiMessageParts.some(
        (p) => ![
          "text",
          "source-url",
          "data-source-url",
          "data-follow-ups",
          "tool-call",
          "tool-result",
          "tool-search_products",
          "tool-analyze_query"
        ].includes(p.type)
      ),
      [uiMessageParts]
    );
    useLayoutEffect(() => {
      if (!isUser || !userMessageRef.current) return;
      const el = userMessageRef.current;
      const textNode = el.firstChild;
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const rects = range.getClientRects();
        setIsMultiline(rects.length > 1);
      } else {
        const computedStyle = window.getComputedStyle(el);
        const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.5;
        setIsMultiline(el.scrollHeight > lineHeight * 1.5);
      }
    }, [isUser, content]);
    useLayoutEffect(() => {
      if (isUser || isTyping || hasWidgetParts) {
        setAssistantBubbleWidth(void 0);
        return;
      }
      const el = assistantContentRef.current;
      if (!el) {
        setAssistantBubbleWidth(void 0);
        return;
      }
      const containerLeft = el.getBoundingClientRect().left;
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let maxRight = 0;
      let node;
      while (node = walker.nextNode()) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const rects = range.getClientRects();
        for (let i = 0; i < rects.length; i++) {
          maxRight = Math.max(maxRight, rects[i].right);
        }
      }
      if (maxRight > 0) {
        const textWidth = maxRight - containerLeft;
        const computedStyle = window.getComputedStyle(el);
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
        const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
        const neededWidth = Math.ceil(textWidth) + paddingLeft + paddingRight + 2;
        if (neededWidth < el.offsetWidth - 8) {
          setAssistantBubbleWidth(neededWidth);
        } else {
          setAssistantBubbleWidth(void 0);
        }
      } else {
        setAssistantBubbleWidth(void 0);
      }
    }, [isUser, isTyping, content, hasUiMessageParts, hasWidgetParts]);
    useLayoutEffect(() => {
      var _a;
      if (isUser || isTyping || isStreaming || hasWidgetParts) {
        setIsRenderedEmpty(false);
        return;
      }
      const el = assistantContentRef.current;
      if (!el) return;
      const hasVisibleChild = (((_a = el.textContent) == null ? void 0 : _a.trim().length) ?? 0) > 0 || el.querySelector(
        'img, svg, canvas, video, iframe, [class*="phc-"], [class*="concierge-product"], [class*="concierge-section"]'
      ) !== null;
      setIsRenderedEmpty(!hasVisibleChild);
    }, [isUser, isTyping, isStreaming, content, hasUiMessageParts, uiMessageParts, hasWidgetParts]);
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (error) {
        toast.error("Failed to copy message");
      }
    };
    const handleShare = () => {
      if (!conversationId) {
        console.warn("Cannot share: missing conversationId");
        return;
      }
      onOpenShareDialog == null ? void 0 : onOpenShareDialog(conversationId, messageIndex);
    };
    const handleLike = async () => {
      var _a;
      if (!conversationId || messageIndex === void 0) return;
      const newState = !liked;
      setLiked(newState);
      setDisliked(false);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? "like" : null,
          headersToInject: headers,
          apiBaseUrl
        });
        if (onUpdateFeedback && ((_a = result.message.metadata) == null ? void 0 : _a.feedback)) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error("Failed to update feedback:", error);
        setLiked(!newState);
      }
    };
    const handleDislike = async () => {
      var _a;
      if (!conversationId || messageIndex === void 0) return;
      const newState = !disliked;
      setDisliked(newState);
      setLiked(false);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await sendMessageFeedback({
          conversationId,
          messageIndex,
          feedbackType: newState ? "dislike" : null,
          headersToInject: headers,
          apiBaseUrl
        });
        if (onUpdateFeedback && ((_a = result.message.metadata) == null ? void 0 : _a.feedback)) {
          onUpdateFeedback(messageIndex, result.message.metadata.feedback);
        }
      } catch (error) {
        console.error("Failed to update feedback:", error);
        setDisliked(!newState);
      }
    };
    const handleFollowUpClick = (query) => {
      if (!isLastMessage || selectedFollowUp) return;
      setLocalSelectedFollowUp(query);
      onSearch == null ? void 0 : onSearch(query);
    };
    const getJustifyContent = (alignment) => {
      switch (alignment) {
        case "right":
          return "flex-end";
        case "center":
          return "center";
        default:
          return "flex-start";
      }
    };
    const renderUserMessageBubble = () => /* @__PURE__ */ jsx(
      MessageBubbleWidget,
      {
        widgetId: "concierge-user-message",
        displayName: "User Message Bubble",
        initialState: {
          bgColor: customColor(brandclub_colors.blueWhite),
          textColor: themeColor("textPrimary"),
          maxWidth: "90%",
          alignment: "right"
        },
        children: (styles, resolveColor) => {
          const borderRadius = isMultiline ? styles.borderRadius : styles.borderRadiusShort;
          const fontFamily = styles.fontFamilyCss ? styles.fontFamilyCss !== "inherit" ? styles.fontFamilyCss : "var(--concierge-user-message-font-family, inherit)" : "var(--concierge-user-message-font-family, inherit)";
          return /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                display: "flex",
                flexDirection: "column",
                alignItems: getJustifyContent(styles.alignment),
                width: "100%"
              },
              children: /* @__PURE__ */ jsx("div", { style: { maxWidth: styles.maxWidth }, children: /* @__PURE__ */ jsx(
                "div",
                {
                  className: "message-content user-message",
                  style: {
                    maxWidth: "100%",
                    minWidth: "50px",
                    backgroundColor: resolveColor(styles.bgColor),
                    color: resolveColor(styles.textColor),
                    borderRadius: `${borderRadius}px`,
                    wordWrap: "break-word",
                    padding: `${styles.paddingY}px ${styles.paddingX}px`,
                    fontSize: `${styles.fontSize}px`,
                    fontWeight: styles.fontWeight,
                    fontFamily,
                    lineHeight: styles.lineHeight,
                    ...!isMultiline && styles.singleLineHeight > 0 ? {
                      minHeight: styles.singleLineHeight,
                      display: "flex",
                      alignItems: "center",
                      boxSizing: "border-box"
                    } : {}
                  },
                  children: /* @__PURE__ */ jsx("div", { ref: userMessageRef, style: { whiteSpace: "pre-wrap" }, children: content })
                }
              ) })
            }
          );
        }
      }
    );
    const renderAssistantMessageBubble = () => /* @__PURE__ */ jsx(
      MessageBubbleWidget,
      {
        widgetId: "concierge-assistant-message",
        displayName: "Assistant Message Bubble",
        initialState: {
          bgColor: themeColor("background"),
          textColor: themeColor("textPrimary"),
          maxWidth: "100%",
          paddingX: 0,
          paddingY: 0,
          alignment: "left"
        },
        children: (styles, resolveColor) => /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              alignItems: getJustifyContent(styles.alignment),
              width: "100%"
            },
            children: /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  maxWidth: styles.maxWidth,
                  width: hasWidgetParts ? "100%" : assistantBubbleWidth ?? void 0,
                  color: resolveColor(styles.textColor)
                },
                children: [
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      ref: assistantContentRef,
                      className: "message-content",
                      style: {
                        maxWidth: "100%",
                        minWidth: "50px",
                        backgroundColor: resolveColor(styles.bgColor),
                        color: resolveColor(styles.textColor),
                        borderRadius: `${styles.borderRadius}px`,
                        wordWrap: "break-word",
                        padding: `${styles.paddingY}px ${styles.paddingX}px`,
                        fontSize: `${styles.fontSize}px`,
                        fontWeight: styles.fontWeight,
                        lineHeight: styles.lineHeight
                      },
                      children: [
                        hasUiMessageParts ? /* @__PURE__ */ jsx(
                          ConciergeMessagePart,
                          {
                            parts: uiMessageParts,
                            onProductClick: onVariantSelect,
                            isStreaming,
                            pdpStacklineSku,
                            isOnChatWidget,
                            apiBaseUrl
                          }
                        ) : /* @__PURE__ */ jsx(
                          "div",
                          {
                            className: "message-markdown",
                            style: { display: "flex", flexDirection: "column" },
                            dangerouslySetInnerHTML: { __html: marked.parse(content) }
                          }
                        ),
                        orders && orders.length > 0 && /* @__PURE__ */ jsx(OrderCardList, { orders })
                      ]
                    }
                  ),
                  bottomRef && /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        height: "1px",
                        width: "100%"
                      },
                      ref: bottomRef
                    }
                  ),
                  viewMode !== "read-only" && !isTyping && !isStreaming && content.length > 0 ? /* @__PURE__ */ jsxs(Actions, { className: "tw:mt-2", children: [
                    /* @__PURE__ */ jsx(
                      Action,
                      {
                        label: "Copy",
                        tooltip: copied ? "Response copied!" : "Copy response",
                        tooltipOpen: copied || void 0,
                        onClick: handleCopy,
                        children: /* @__PURE__ */ jsx(CopyIcon, { width: 20, height: 20 })
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      Action,
                      {
                        label: "Like",
                        tooltip: liked ? "Thanks for your feedback!" : "Good response",
                        onClick: handleLike,
                        className: liked ? "tw:bg-[#DCDDDC]" : "",
                        children: /* @__PURE__ */ jsx(ThumbsUpIcon, { width: 20, height: 20, className: liked ? "tw:fill-current" : "" })
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      Action,
                      {
                        label: "Dislike",
                        tooltip: disliked ? "Thanks for your feedback!" : "Bad response",
                        onClick: handleDislike,
                        className: disliked ? "tw:bg-[#DCDDDC]" : "",
                        children: /* @__PURE__ */ jsx(ThumbsDownIcon, { width: 20, height: 20, className: disliked ? "tw:fill-current" : "" })
                      }
                    ),
                    !isSharedView && !isOnChatWidget && /* @__PURE__ */ jsx(Action, { label: "Share", tooltip: "Share", onClick: handleShare, children: /* @__PURE__ */ jsx(ShareIcon, { width: 20, height: 20 }) }),
                    isLastMessage && !isSharedView && onRetry && /* @__PURE__ */ jsx(Action, { label: "Retry", tooltip: "Try again", onClick: () => onRetry(), children: /* @__PURE__ */ jsx(RetryIcon, {}) })
                  ] }) : null,
                  followUpsLoading && /* @__PURE__ */ jsx(
                    "div",
                    {
                      style: {
                        padding: "4px 0 8px",
                        color: "var(--color-text-secondary, #9ca3af)",
                        fontSize: "13px",
                        letterSpacing: "0.1em"
                      },
                      children: "..."
                    }
                  ),
                  !isTyping && !isStreaming && followUpQueries.length > 0 && !(nextUserMessage && !selectedFollowUp) && /* @__PURE__ */ jsx(FollowUpQueriesWidget, { widgetId: "concierge-follow-up-queries", displayName: "Follow-up Suggestions", children: (widgetStyles, resolveColor2) => {
                    const isDisabled = !isLastMessage || !!selectedFollowUp;
                    const hex = (c) => !c || c.startsWith("#") || c.startsWith("rgb") ? c : `#${c}`;
                    const defaultBg = hex(resolveColor2(widgetStyles.pillBgColor));
                    const defaultText = hex(resolveColor2(widgetStyles.pillTextColor));
                    const defaultBorder = hex(resolveColor2(widgetStyles.pillBorderColor));
                    const hoverBg = defaultText;
                    const hoverText = defaultBg;
                    const hoverBorder = defaultText;
                    const selectedBg = hex(resolveColor2(widgetStyles.pillBgColorSelected));
                    const selectedText = hex(resolveColor2(widgetStyles.pillTextColorSelected));
                    const selectedBorder = hex(resolveColor2(widgetStyles.pillBorderColorSelected));
                    return /* @__PURE__ */ jsxs(
                      "div",
                      {
                        className: "fuq-wrap",
                        style: {
                          display: "flex",
                          flexWrap: "wrap",
                          marginTop: 16,
                          gap: `${widgetStyles.containerGap}px`,
                          width: "100%"
                        },
                        children: [
                          /* @__PURE__ */ jsx("style", { children: `
                          .fuq-wrap .fuq-pill {
                            background-color: ${defaultBg};
                            color: ${defaultText};
                            border-color: ${defaultBorder};
                            transition: all 0.2s ease;
                          }
                          .fuq-wrap .fuq-pill:not(:disabled):not(.fuq-selected):hover {
                            background-color: ${hoverBg};
                            color: ${hoverText};
                            border-color: ${hoverBorder};
                          }
                          .fuq-wrap .fuq-pill.fuq-selected {
                            background-color: ${selectedBg};
                            color: ${selectedText};
                            border-color: ${selectedBorder};
                          }
                        ` }),
                          followUpQueries.map((query, index) => {
                            const isSelected = selectedFollowUp === query;
                            return /* @__PURE__ */ jsx(
                              "button",
                              {
                                className: `fuq-pill${isSelected ? " fuq-selected" : ""}`,
                                onClick: () => handleFollowUpClick(query),
                                disabled: isDisabled && !isSelected,
                                style: {
                                  padding: `${widgetStyles.pillPaddingY}px ${widgetStyles.pillPaddingX}px`,
                                  borderRadius: `${widgetStyles.pillBorderRadius}px`,
                                  borderWidth: widgetStyles.pillBorderWidth,
                                  borderStyle: "solid",
                                  fontSize: `${widgetStyles.pillFontSize}px`,
                                  fontWeight: widgetStyles.pillFontWeight,
                                  lineHeight: "22px",
                                  textAlign: "left",
                                  cursor: isSelected ? "default" : isDisabled ? "not-allowed" : "pointer",
                                  opacity: !isSelected && isDisabled ? 0.5 : 1
                                },
                                children: query
                              },
                              index
                            );
                          })
                        ]
                      }
                    );
                  } })
                ]
              }
            )
          }
        )
      }
    );
    if (!isTyping && !hasAnyContent && !shimmerActive) return null;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref: messageWrapperRef,
        className: `message ${role}`,
        style: {
          display: isRenderedEmpty ? "none" : "flex",
          flexDirection: "column",
          width: "100%",
          marginBottom: hasWidgetParts ? 16 : void 0
        },
        children: [
          !isUser && devInfo && /* @__PURE__ */ jsx(DevInfoPanel, { devInfo }),
          !isUser && isTyping && /* @__PURE__ */ jsx(ChainOfThoughtWidget, { widgetId: "chain-of-thought", displayName: "Chain of Thought", children: (styles, resolveColor) => /* @__PURE__ */ jsxs("div", { style: { padding: `${styles.thinkingPaddingY}px ${styles.thinkingPaddingX}px` }, children: [
            /* @__PURE__ */ jsx("style", { children: `@keyframes cc-pulse{0%,100%{transform:scale(1)}50%{transform:scale(0.5)}}` }),
            /* @__PURE__ */ jsx(
              "span",
              {
                style: {
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  backgroundColor: resolveColor(styles.thinkingTextColor),
                  borderRadius: "50%",
                  animation: "cc-pulse 1s ease-in-out infinite"
                }
              }
            )
          ] }) }),
          !isUser && shimmerActive && /* @__PURE__ */ jsx(ChainOfThoughtWidget, { widgetId: "chain-of-thought", displayName: "Chain of Thought", children: (styles, resolveColor) => styles.thinkingEnabled ? /* @__PURE__ */ jsx("div", { style: { maxWidth: "100%" }, children: /* @__PURE__ */ jsx(
            "div",
            {
              className: "message-content",
              style: {
                maxWidth: "100%",
                backgroundColor: "none",
                color: resolveColor(styles.thinkingTextColor),
                borderRadius: `${styles.thinkingBorderRadius}px`,
                borderBottomLeftRadius: `${styles.thinkingBorderRadiusBottomLeft}px`,
                borderBottomRightRadius: `${styles.thinkingBorderRadius}px`,
                wordWrap: "break-word",
                padding: `${styles.thinkingPaddingY}px ${styles.thinkingPaddingX}px`
              },
              children: /* @__PURE__ */ jsx(
                Shimmer,
                {
                  as: "p",
                  style: { fontSize: `${styles.thinkingTextFontSize}px`, fontFamily: "inherit" },
                  duration: styles.shimmerDuration,
                  color: resolveColor(styles.thinkingTextColor),
                  highlightColor: resolveColor(styles.shimmerColor),
                  children: transientStatus && transientStatus !== "thinking" ? transientStatus : styles.thinkingText
                }
              )
            }
          ) }) : null }),
          isUser && hasUiMessageParts && (() => {
            const imageFiles = uiMessageParts.filter(
              (p) => {
                var _a;
                return p.type === "file" && ((_a = p.mediaType) == null ? void 0 : _a.startsWith("image/"));
              }
            );
            if (imageFiles.length === 0) return null;
            return /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  display: "flex",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                  width: "100%",
                  marginBottom: "0.5rem"
                },
                children: imageFiles.map((fp, i) => {
                  const size = imageFiles.length === 1 ? isMobile ? 250 : 256 : isMobile ? 96 : 128;
                  return /* @__PURE__ */ jsx(
                    "div",
                    {
                      onClick: () => setPreviewImageUrl(fp.url),
                      style: {
                        width: `${size}px`,
                        height: `${size}px`,
                        borderRadius: "12px",
                        overflow: "hidden",
                        backgroundColor: "#f9f9f9",
                        cursor: "zoom-in"
                      },
                      children: /* @__PURE__ */ jsx(
                        "img",
                        {
                          src: fp.url,
                          alt: fp.filename || "Uploaded image",
                          style: {
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            mixBlendMode: "multiply"
                          }
                        }
                      )
                    },
                    i
                  );
                })
              }
            );
          })(),
          !isTyping && (isUser ? renderUserMessageBubble() : renderAssistantMessageBubble()),
          previewImageUrl && /* @__PURE__ */ jsx(ImagePreviewOverlay, { src: previewImageUrl, onClose: closePreview })
        ]
      }
    );
  }
);
function uiPartsFingerprint(parts) {
  if (!(parts == null ? void 0 : parts.length)) return "";
  return `${parts.length}:${parts.map((p) => {
    var _a, _b;
    if (p.type === "text") return `text|${((_a = p.text) == null ? void 0 : _a.length) ?? 0}`;
    if (p.type === "data-product-carousels" || p.type === "data-section") return `ds|${((_b = p.data) == null ? void 0 : _b.title) ?? ""}`;
    return `${p.type}|${p.state ?? ""}`;
  }).join(",")}`;
}
const ChatMessage = React__default.memo(ChatMessageComponent, (prevProps, nextProps) => {
  var _a, _b;
  return prevProps.role === nextProps.role && prevProps.content === nextProps.content && prevProps.isTyping === nextProps.isTyping && prevProps.bottomRef === nextProps.bottomRef && prevProps.isStreaming === nextProps.isStreaming && prevProps.isLastMessage === nextProps.isLastMessage && ((_a = prevProps.followUpQueries) == null ? void 0 : _a.length) === ((_b = nextProps.followUpQueries) == null ? void 0 : _b.length) && prevProps.nextUserMessage === nextProps.nextUserMessage && prevProps.orders === nextProps.orders && prevProps.onSearch === nextProps.onSearch && prevProps.onVariantSelect === nextProps.onVariantSelect && prevProps.onSuggestionClick === nextProps.onSuggestionClick && uiPartsFingerprint(prevProps.uiMessageParts) === uiPartsFingerprint(nextProps.uiMessageParts);
});
export {
  ChatMessage
};
//# sourceMappingURL=ChatMessage.js.map
